import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Flame, PieChart, Dumbbell, Ruler, Heart, Timer, FlaskConical, ArrowRight, Bookmark, Zap, Crosshair, Gauge, Syringe, ClipboardList, Repeat, Shield, Beaker, Scale, Leaf, DollarSign } from 'lucide-react';
import { PageSEO } from '../components/seo/SchemaMarkup';
import { ShareButton } from '../components/ShareButton';

const tools = [
  {
    title: 'HYROX Finish Time Predictor',
    description: 'Enter your fitness benchmarks and get a predicted HYROX finish time with station-by-station splits and race strategy.',
    icon: Zap,
    href: '/tools/hyrox-finish-time',
    color: '#e8ff00',
    category: 'HYROX',
    featured: true,
  },
  {
    title: 'Protein Calculator',
    description: 'Calculate your daily protein needs with meal timing recommendations based on your weight, goals, and activity level.',
    icon: Calculator,
    href: '/tools/protein-calculator',
    color: '#E63946',
    category: 'Nutrition',
  },
  {
    title: 'TDEE & Calorie Calculator',
    description: 'Find your Total Daily Energy Expenditure using the Mifflin-St Jeor equation. Get cut and bulk calorie targets.',
    icon: Flame,
    href: '/tools/tdee-calculator',
    color: '#ff6b35',
    category: 'Nutrition',
  },
  {
    title: 'Macro Calculator',
    description: 'Get your ideal protein, carb, and fat split tailored to your body composition and fitness goals.',
    icon: PieChart,
    href: '/tools/macro-calculator',
    color: '#00F0FF',
    category: 'Nutrition',
  },
  {
    title: 'One Rep Max Calculator',
    description: 'Estimate your 1RM using Epley, Brzycki, and Lombardi formulas. Includes a full percentage training chart.',
    icon: Dumbbell,
    href: '/tools/one-rep-max',
    color: '#E63946',
    category: 'Strength',
  },
  {
    title: 'Body Fat Estimator',
    description: 'Estimate your body fat percentage using the US Navy method with waist, neck, and hip measurements.',
    icon: Ruler,
    href: '/tools/body-fat-calculator',
    color: '#FFD100',
    category: 'Body Comp',
  },
  {
    title: 'Heart Rate Zones',
    description: 'Calculate your 5 training heart rate zones using Karvonen or max HR method. Perfect for HYROX prep.',
    icon: Heart,
    href: '/tools/heart-rate-zones',
    color: '#22c55e',
    category: 'Cardio',
  },
  {
    title: 'Focus & Recovery Timers',
    description: 'Pomodoro focus sessions, meditation timers, breathwork patterns, and cold exposure countdowns.',
    icon: Timer,
    href: '/timers',
    color: '#00F0FF',
    category: 'Wellness',
  },
  {
    title: 'Recovery Payback Calculator',
    description: 'For gym owners: payback period for 11 recovery amenities — sauna, cold plunge, contrast suite, red light and more. Edit every assumption, rankings update live.',
    icon: DollarSign,
    href: '/tools/recovery-payback-calculator',
    color: '#C7FA3E',
    category: 'Recovery',
  },
  {
    title: 'Peptide Reconstitution Calculator',
    description: 'Calculate exact dosing and syringe draw volumes for peptide reconstitution with BAC water.',
    icon: FlaskConical,
    href: '/tools/peptide-calculator',
    color: '#E63946',
    category: 'Peptides',
  },
  {
    title: 'Peptide Dosing Calculator',
    description: 'Get recommended peptide doses based on peptide type, body weight, and experience level. Covers BPC-157, TB-500, Semaglutide, and more.',
    icon: Syringe,
    href: '/tools/peptide-dosing',
    color: '#E63946',
    category: 'Peptides',
  },
  {
    title: 'Station Split Calculator',
    description: 'Break down your HYROX station target times based on your goal finish time and running pace. Printable pace card included.',
    icon: Crosshair,
    href: '/tools/station-split',
    color: '#e8ff00',
    category: 'HYROX',
  },
  {
    title: 'Race Pace Planner',
    description: 'Build a downloadable HYROX pace card with target splits for every run and station segment.',
    icon: Gauge,
    href: '/tools/race-pace',
    color: '#e8ff00',
    category: 'HYROX',
  },
  {
    title: 'Longevity Score Assessment',
    description: 'Deep 29-question assessment across 8 science-backed longevity habits. Get per-habit scores, gap analysis, and personalized peptide recommendations.',
    icon: Leaf,
    href: '/tools/longevity-score',
    color: '#c8ff00',
    category: 'Longevity',
    featured: true,
  },
  {
    title: 'Biohacking Score Quiz',
    description: 'Rate your biohacking protocol across 6 categories. Get a personalized score, radar chart, and tailored recommendations.',
    icon: Shield,
    href: '/tools/biohacking-score',
    color: '#E63946',
    category: 'Biohacking',
    featured: true,
  },
  {
    title: 'Recovery Stack Builder',
    description: 'Build your personalized recovery protocol. Compare HydroTherapy, cold plunge, peptides, supplements & more. Get a stack score and weekly schedule.',
    icon: Shield,
    href: '/tools/recovery-stack',
    color: '#00F0FF',
    category: 'Recovery',
    featured: true,
  },
  {
    title: 'Peptide Stack Builder',
    description: 'Build your complete peptide protocol. Select peptides, get reconstitution guides, dosing schedules, and cycle timelines in one workflow.',
    icon: FlaskConical,
    href: '/tools/peptide-stack',
    color: '#E63946',
    category: 'Peptides',
    featured: true,
  },
  {
    title: 'Reconstitution Guide',
    description: 'Step-by-step peptide reconstitution with BAC water. Calculate exact syringe draw volumes for precise dosing.',
    icon: Beaker,
    href: '/tools/reconstitution-guide',
    color: '#E63946',
    category: 'Peptides',
  },
  {
    title: 'Body Recomp Tracker',
    description: 'Track your body recomposition progress with weight, body fat, and muscle mass over time. Visual trend charts.',
    icon: ClipboardList,
    href: '/tools/body-recomp',
    color: '#00F0FF',
    category: 'Body Comp',
  },
  {
    title: 'Peptide Comparison Tool',
    description: 'Compare 2-3 peptides side-by-side. Dosing, effects, cost, safety profiles, and stacking compatibility all in one view.',
    icon: Scale,
    href: '/tools/peptide-compare',
    color: '#f59e0b',
    category: 'Peptides',
    featured: true,
  },
  {
    title: 'Peptide Cycle Tracker',
    description: 'Log and track your peptide cycles with dosing, timing, on/off scheduling, and visual timeline.',
    icon: Repeat,
    href: '/tools/peptide-cycle',
    color: '#E63946',
    category: 'Peptides',
  },
  {
    title: 'Protocol Tracker',
    description: 'Log daily peptide doses, track side effects, monitor your personal dashboard, and share anonymized data with the community.',
    icon: ClipboardList,
    href: '/tools/protocol-tracker',
    color: '#22c55e',
    category: 'Peptides',
    featured: true,
  },
];

export default function ToolsPage() {
  return (
    <main className="pt-20 min-h-screen" data-testid="tools-page">
      <PageSEO
        title="Free Fitness & Peptide Calculators | NinjAthlete Tools"
        description="Free calculators for peptide dosing, macros, TDEE, body fat, 1RM, heart rate zones, and more. Science-backed tools for athletes and biohackers."
        path="/tools"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Tools', url: '/tools' }]}
      />
      <section className="container-ninja section-spacing">
        <div className="text-center mb-12">
          <p className="text-ninja-label text-[#00F0FF] mb-4 animate-entrance" style={{ animationDelay: '0.1s' }}>
            Free Performance Tools
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-heading mb-4 animate-entrance" style={{ animationDelay: '0.2s' }} data-testid="tools-page-title">
            Optimize <span className="text-gradient-ninja">Everything</span>
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto animate-entrance" style={{ animationDelay: '0.3s' }}>
            Science-backed calculators and tools for nutrition, training, body composition, and recovery. No sign-up required.
          </p>
          <Link to="/my-results" className="inline-flex items-center gap-2 mt-4 text-[#00F0FF] text-sm hover:text-white transition-colors animate-entrance" style={{ animationDelay: '0.4s' }} data-testid="my-results-link">
            <Bookmark className="w-4 h-4" /> View My Saved Results <ArrowRight className="w-3 h-3" />
          </Link>
          <div className="mt-4 flex justify-center animate-entrance" style={{ animationDelay: '0.5s' }}>
            <ShareButton title="Free Fitness & Peptide Calculators | NinjAthlete" path="/tools" accentColor="#00F0FF" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {tools.map((tool, i) => {
            const isComingSoon = tool.comingSoon;
            const Wrapper = isComingSoon ? 'div' : Link;
            const wrapperProps = isComingSoon
              ? {}
              : { to: tool.href };
            return (
              <Wrapper
                key={tool.title}
                {...wrapperProps}
                className={`group bg-zinc-900/50 border p-6 transition-all duration-300 animate-entrance flex flex-col relative ${
                  tool.featured
                    ? 'border-[#e8ff00]/30 hover:border-[#e8ff00]/60 md:col-span-2'
                    : isComingSoon
                    ? 'border-zinc-800/50 opacity-60 cursor-default'
                    : 'border-zinc-800 hover:border-[#E63946]/40'
                }`}
                style={{ animationDelay: `${0.05 * (i + 1)}s` }}
                data-testid={`tool-card-${tool.href.split('/').pop() || tool.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {isComingSoon && (
                  <span className="absolute top-3 right-3 font-mono text-[9px] tracking-wider text-zinc-500 bg-zinc-800 px-2 py-0.5 uppercase">Coming Soon</span>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center border border-zinc-700 group-hover:border-opacity-0 transition-all" style={{ backgroundColor: `${tool.color}15` }}>
                    <tool.icon className="w-5 h-5" style={{ color: tool.color }} />
                  </div>
                  {!isComingSoon && <span className="text-[9px] uppercase tracking-wider text-zinc-600 bg-zinc-800 px-2 py-1">{tool.category}</span>}
                </div>
                <h3 className={`text-lg font-heading mb-2 transition-colors ${
                  tool.featured ? 'text-[#e8ff00] group-hover:text-white' : 'text-white group-hover:text-[#00F0FF]'
                }`}>{tool.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed flex-1">{tool.description}</p>
                {!isComingSoon && (
                  <div className="mt-4 flex items-center gap-1 text-[#E63946] text-xs font-heading uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                    Open Tool <ArrowRight className="w-3 h-3" />
                  </div>
                )}
              </Wrapper>
            );
          })}
        </div>
      </section>
    </main>
  );
}
